import { MRT_ColumnDef } from 'material-react-table';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import {
  sizeTab,
  TABLE_CONSTANTS_COLUMS,
  useCheckPermissions,
  useTableServerSideFiltering,
} from '@/shared';
import {
  CustomSearch,
  CustomTabPanel,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from '@/shared/components/ui';
import { useTableFilter } from '@/shared/hooks/useTableFilter';
import { Router } from '@/shared/interfaces';
import {
  emptyCellOneLevel,
  formatDateWithTime,
  hasAllPermissions,
  hasPermission,
} from '@/shared/utils';
import {
  useDeleteRouter,
  useFetchRouters,
  useUpdateRouter,
} from '@/store/erp/router';
import { useUiConfirmModalStore } from '@/store/ui';
import { SAVE_ROUTER_PERMISSIONS } from '../../shared/constants';
import { Box, Tabs, Tab } from '@mui/material';
import PruebaConectividad from '../components/PruebaConectividad'

export type RoutesrPageProps = {};

const RoutesrPage: React.FC<RoutesrPageProps> = () => {
  useCheckPermissions(PermissionsEnum.api_view_router);
  const returnUrlRoutesrPage = '/configuracion-red/routers';
  const navigate = useNavigate();

  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* global state -----------------------
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );

  ///* mutations -----------------------
  const updateRouter = useUpdateRouter({
    navigate,
    returnUrl: returnUrlRoutesrPage,
  });
  const deleteRouter = useDeleteRouter();

  ///* table -----------------------
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
    cleanFilter
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data -----------------------
  const {
    data: RoutesrPagingRes,
    isLoading,
    isRefetching,
  } = useFetchRouters({
    page: pageIndex + 1,
    page_size: pageSize,
    nombre: searchTerm,
    ...filterObject,
    filterByState: false,
  });

  ///* handlers
  const onEdit = (router: Router) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Editar Router',
      subtitle: '¿Está seguro que desea editar este Router?',
      onConfirm: () => {
        setConfirmDialogIsOpen(false);
        navigate(`${returnUrlRoutesrPage}/editar/${router.id_router}`);
      },
    });
  };

  const onDelete = (router: Router) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Eliminar Router',
      subtitle: '¿Está seguro que desea eliminar este Router?',
      onConfirm: () => {
        setConfirmDialogIsOpen(false);
        deleteRouter.mutate(router.id_router!);
      },
    });
  };

  ///* columns
  const columns = useMemo<MRT_ColumnDef<Router>[]>(
    () => [
      {
        accessorKey: 'nombre',
        header: 'Nombre',
        size: 180,
        Cell: ({ row }) => emptyCellOneLevel(row, 'nombre'),
      },

      {
        accessorKey: 'direccion',
        header: 'Ubicacion',
        size: 180,
        Cell: ({ row }) => emptyCellOneLevel(row, 'direccion'),
      },

      {
        accessorKey: 'ip',
        header: 'Ip',
        size: 180,
        Cell: ({ row }) => emptyCellOneLevel(row, 'ip'),
      },

      // {
      //   accessorKey: 'user',
      //   header: 'User',
      //   size: 180,
      //   Cell: ({ row }) => emptyCellOneLevel(row, 'user'),
      // },

      // {
      //   accessorKey: 'password',
      //   header: 'Password',
      //   size: 180,
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   Cell: ({ row }) => {
      //     return (
      //       <PasswordTableCell password={row.original?.password || 'N/A'} />
      //     );
      //   },
      // },

      // {
      //   accessorKey: 'clave_api',
      //   header: 'Clave api',
      //   size: 180,
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   Cell: ({ row }) => {
      //     return (
      //       <PasswordTableCell password={row.original?.clave_api || 'N/A'} />
      //     );
      //   },
      // },
      {
        accessorKey: 'puerto_api',
        header: 'Puerto api',
        size: 180,
        Cell: ({ row }) => emptyCellOneLevel(row, 'puerto_api'),
      },
      {
        accessorKey: 'usuario_api',
        header: 'Usuario api',
        size: 180,
        Cell: ({ row }) => emptyCellOneLevel(row, 'usuario_api'),
      },
      {
        accessorKey: 'tipo_router',
        header: 'Tipo router',
        size: 180,
        Cell: ({ row }) => row.original?.tipo_router || 'N/A',
      },
      {
        accessorKey: 'nodo',
        header: 'Nodo',
        size: 180,
        Cell: ({ row }) =>
          row?.original?.nodo_data?.nombre ||
          row?.original?.nodo ||
          'N/A',
      },
      {
        accessorKey: 'olt',
        header: 'OLT',
        size: 180,
        Cell: ({ row }) =>
          row?.original?.olt_data?.nombre ||
          row?.original?.olt ||
          'N/A',
      },

      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          return (
            <SwitchComponente
              estadoSwitch={row.original?.estado}
              titulo={row.original?.estado ? 'Activo' : 'Inactivo'}
              setEstadoSwitch={state => {
                if (!hasPermission(PermissionsEnum.api_change_router)) return;

                setConfirmDialog({
                  isOpen: true,
                  title: 'Cambiar Estado',
                  subtitle:
                    '¿Está seguro que desea cambiar el estado de este registro ?',
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    updateRouter.mutate({
                      id: row.original?.id_router!,
                      data: { estado: !!state } as any,
                    });
                  },
                });
              }}
            />
          );
        },
      },

      {
        accessorKey: 'fecha_creacion',
        header: 'Fecha de Creación',
        size: 200,
        Cell: ({ row }) =>
          row.original?.fecha_creacion
            ? formatDateWithTime(row.original.fecha_creacion)
            : 'N/A',
      },
      {
        accessorKey: 'fecha_modificacion',
        header: 'Fecha de Modificación',
        size: 200,
        Cell: ({ row }) =>
          row.original?.fecha_modificacion
            ? formatDateWithTime(row.original.fecha_modificacion)
            : 'N/A',
      },
    ],
    []
  );


  const [tabValue, setTabValue] = useState(0);
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // function TabPanel(props: TabPanelProps) {
  //   const { children, value, index, ...other } = props;
  //   return (
  //     <div
  //       role="tabpanel"
  //       hidden={value !== index}
  //       id={`tabpanel-${index}`}
  //       aria-labelledby={`tab-${index}`}
  //       {...other}
  //     >
  //       {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  //     </div>
  //   );
  // }

  return (
    <>
      <SingleTableBoxScene
        title="Router"
        urlRedirect={`${returnUrlRoutesrPage}/crear`}
        enableRedirect={hasAllPermissions([
          ...SAVE_ROUTER_PERMISSIONS,
          PermissionsEnum.api_add_router,
        ])}
      >
          <Box sx={{ bgcolor: 'background.paper', maxWidth: sizeTab, paddingBottom: 2 }} >
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Gestion de Router" id="tab-0" aria-controls="tabpanel-0" />
              <Tab label="Prueba de Conectividad" id="tab-1" aria-controls="tabpanel-1" />
            </Tabs>
          </Box>

          <CustomTabPanel value={tabValue} index={0}>
            <CustomSearch
              onChange={onChangeFilter}
              value={globalFilter}
              text="por nombre de router"
              cleanFilter={cleanFilter}
            />

            <GeneralTable<Router>
              columns={columns}
              data={RoutesrPagingRes?.data?.data || []}
              isLoading={isLoading}
              isRefetching={isRefetching}
              // // search
              enableGlobalFilter={false}
              // // filters - llega al back
              enableManualFiltering={true}
              columnFilters={columnFilters}
              onColumnFiltersChange={setColumnFilters}
              // // pagination
              pagination={pagination}
              onPaging={setPagination}
              rowCount={RoutesrPagingRes?.data?.count}
              // // actions ========
              enableColumnActions={hasAllPermissions([
                ...SAVE_ROUTER_PERMISSIONS,
                PermissionsEnum.api_change_router,
              ])}
              actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
              // crud
              canEdit={hasAllPermissions([
                ...SAVE_ROUTER_PERMISSIONS,
                PermissionsEnum.api_add_router,
              ])}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          </CustomTabPanel>

          <CustomTabPanel value={tabValue} index={1}>
            <PruebaConectividad />
          </CustomTabPanel>

      </SingleTableBoxScene>
    </>
  );
};

export default RoutesrPage;
